import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Separate from the one-time Dashboard popup's dismissal key -- this banner
// is an independent, recurring reminder shown on every page, not a
// replacement for the popup. Dismissing it only hides it temporarily --
// it reappears on its own after RESHOW_AFTER_MS so a user who keeps
// avoiding zone access keeps getting nudged instead of it vanishing forever.
const DISMISSED_AT_KEY = 'zonesPersistentBannerDismissedAt';
const RESHOW_AFTER_MS = 60 * 60 * 1000; // 1 hour
const RECHECK_INTERVAL_MS = 60 * 1000; // check every minute while the tab is open

const isCurrentlyDismissed = (): boolean => {
  try {
    const dismissedAt = localStorage.getItem(DISMISSED_AT_KEY);
    if (!dismissedAt) return false;
    return Date.now() - Number(dismissedAt) < RESHOW_AFTER_MS;
  } catch {
    return false;
  }
};

export const ZoneBanner: React.FC = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(isCurrentlyDismissed);

  useEffect(() => {
    const interval = setInterval(() => {
      setDismissed(isCurrentlyDismissed());
    }, RECHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const shouldShow = !!user && !user.is_admin && (user.groups?.length ?? 0) === 0 && !dismissed;

  if (!shouldShow) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    } catch {
      // Private browsing / storage disabled -- banner just reappears next visit.
    }
  };

  return (
    <div
      role="region"
      aria-label="Zone access required"
      className="bg-purple-900/40 border border-purple-700/50 rounded-lg p-3 my-3 mx-6"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-purple-100">
          You&apos;re not in any zone yet. Zones control which MCP servers you can see —{' '}
          <Link to="/zones" className="underline hover:text-purple-50">
            visit My Zones
          </Link>{' '}
          to request access.{' '}
          <span className="text-purple-300">An administrator still needs to approve it.</span>
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-xs text-purple-300 hover:text-purple-100 focus:outline-none flex-shrink-0"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default ZoneBanner;
